import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from '@/components/ui'
import {
  Users,
  Search,
  Plus,
  TrendingUp,
  Clock,
  FileText,
  ArrowRight,
  Loader2,
} from 'lucide-react'
import { jobsApi } from '@/services/api'

interface CustomerSummary {
  id: string
  name: string
  totalAnalyses: number
  lastAnalysisDate: string
  latestRiskScore: number | null
  status: 'active' | 'inactive'
}

export function Customers() {
  const [customers, setCustomers] = useState<CustomerSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        // Get all jobs and group by customer
        const { jobs } = await jobsApi.list({ pageSize: 100 })
        
        // Group jobs by customerId
        const customerMap = new Map<string, CustomerSummary>()
        
        jobs.forEach((job: any) => {
          const existing = customerMap.get(job.customerId)
          if (existing) {
            existing.totalAnalyses++
            if (new Date(job.createdAt) > new Date(existing.lastAnalysisDate)) {
              existing.lastAnalysisDate = job.createdAt
              if (job.status === 'completed' && job.result?.risk_score) {
                existing.latestRiskScore = job.result.risk_score
              }
            }
          } else {
            customerMap.set(job.customerId, {
              id: job.customerId,
              name: job.customerId.replace(/-/g, ' ').replace(/_/g, ' '),
              totalAnalyses: 1,
              lastAnalysisDate: job.createdAt,
              latestRiskScore: job.result?.risk_score || null,
              status: 'active',
            })
          }
        })
        
        setCustomers(Array.from(customerMap.values()))
      } catch (error) {
        console.error('Failed to fetch customers:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchCustomers()
  }, [])

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getRiskBadge = (score: number | null) => {
    if (score === null) return <Badge variant="secondary">No Score</Badge>
    if (score <= 2) return <Badge variant="success">Low Risk</Badge>
    if (score <= 3) return <Badge variant="warning">Medium Risk</Badge>
    return <Badge variant="destructive">High Risk</Badge>
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-muted-foreground">
            View and manage customer profiles and analyses
          </p>
        </div>
        <Link to="/upload">
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Add Customer
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{customers.length}</p>
                <p className="text-sm text-muted-foreground">Total Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-100">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {customers.reduce((sum, c) => sum + c.totalAnalyses, 0)}
                </p>
                <p className="text-sm text-muted-foreground">Total Analyses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {customers.filter(c => c.latestRiskScore !== null).length}
                </p>
                <p className="text-sm text-muted-foreground">With Risk Scores</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer List */}
      <Card>
        <CardHeader>
          <CardTitle>All Customers</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No customers yet</h3>
              <p className="text-muted-foreground mt-1">
                Upload customer data to get started
              </p>
              <Link to="/upload">
                <Button className="mt-4 gap-2">
                  <Plus className="h-4 w-4" />
                  Add Customer
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCustomers.map((customer) => (
                <Link
                  key={customer.id}
                  to={`/history?customer=${customer.id}`}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{customer.name}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {customer.totalAnalyses} analyses
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last: {formatDate(customer.lastAnalysisDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getRiskBadge(customer.latestRiskScore)}
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
